import React, { useMemo, useState, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { PERIODS, periodLabel } from '../utils/financialPeriods';
import { useAppContext } from '../context/AppContext';
import MantleGrid from './MantleGrid';
import { cell, GRID } from '../styles/gridStyles';

// =============================================================================
// PostingSummary
// A read-only rollup shown at the foot of a distribution page: it collapses the
// page's cost lines into one row per (Origin Site · GL account · Cost Centre)
// and shows the value per financial period — i.e. how this page posts to the
// general ledger. Pass `entries`, each
//   { site, gl, glName?, costCentre, values[12] }
// and the component groups and sums them. `glName` is optional: when any entry
// supplies one, a Description column appears; when none do, it does not, so the
// pages still on dummy data do not grow an empty column.
//
// COLLAPSED BY DEFAULT. Most people capturing rates never look at this — it is
// there for the person reconciling to the ledger, and a full-width grid of
// twelve months is a lot of screen to spend on an audience of one. The choice
// is remembered per browser, so opening it once makes it stay open.
// =============================================================================

// Glide draws its horizontal scrollbar inside the grid's own height, so a
// fixed-height container has to leave room for it. Mirrors MantleDataGrid.
const H_SCROLLBAR = 14;

const STORE_KEY = 'mantle-posting-summary-open';

const readOpen = (key) => {
  try { return localStorage.getItem(`${STORE_KEY}:${key}`) === '1'; } catch { return false; }
};

const PostingSummary = ({
  entries = [],
  siteLabel = 'Origin Site',
  title = 'GL Posting Summary — by Site · GL · Cost Centre',
  // Distinguishes one page's remembered state from another's. Defaults to the
  // title so two summaries on different pages do not share a toggle.
  storageKey,
}) => {
  const { fyStartMonth } = useAppContext();
  const key = storageKey || title;
  const [open, setOpen] = useState(() => readOpen(key));

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(`${STORE_KEY}:${key}`, next ? '1' : '0'); } catch { /* full/blocked */ }
      return next;
    });
  }, [key]);

  const grouped = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const k = `${e.site || '—'}|||${e.gl || '—'}|||${e.costCentre || '—'}`;
      if (!map.has(k)) {
        map.set(k, {
          site: e.site || '—',
          gl: e.gl || '—',
          glName: e.glName || '',
          costCentre: e.costCentre || '—',
          values: Array(12).fill(0),
        });
      }
      const g = map.get(k);
      // First non-empty description wins — the account code is the group key,
      // so any description on it is the same account's.
      if (!g.glName && e.glName) g.glName = e.glName;
      (e.values || []).forEach((v, i) => { g.values[i] += (v || 0); });
    });
    return [...map.values()].sort((a, b) => (a.site + a.gl + a.costCentre).localeCompare(b.site + b.gl + b.costCentre));
  }, [entries]);

  const showGlName = useMemo(() => grouped.some((g) => g.glName), [grouped]);

  // A collapsed summary is a heading and nothing else, but the totals are cheap
  // and worth showing on the strip — enough to tell you whether opening it is
  // worth the screen.
  const grand = useMemo(
    () => grouped.reduce((a, g) => a + g.values.reduce((b, v) => b + v, 0), 0),
    [grouped]
  );

  const columns = useMemo(() => ([
    { columnId: 'site', width: 190, resizable: true },
    { columnId: 'gl', width: 110, resizable: true },
    ...(showGlName ? [{ columnId: 'glName', width: 220, resizable: true }] : []),
    { columnId: 'cc', width: 140, resizable: true },
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 78, resizable: true })),
    { columnId: 'annual', width: 116, resizable: true },
  ]), [showGlName]);

  const rows = useMemo(() => {
    const colTotals = PERIODS.map((_, i) => grouped.reduce((a, g) => a + g.values[i], 0));
    const total = colTotals.reduce((a, v) => a + v, 0);
    const header = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header(siteLabel),
        cell.header('GL Account'),
        ...(showGlName ? [cell.header('Description')] : []),
        cell.header('Cost Centre'),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
        cell.header('Annual', { align: 'right' }),
      ],
    };
    const data = grouped.map((g, idx) => ({
      rowId: `g${idx}`,
      height: GRID.size.dataRow,
      cells: [
        cell.label(g.site, { idx }),
        cell.text(g.gl, { idx }),
        ...(showGlName ? [cell.text(g.glName, { idx })] : []),
        cell.text(g.costCentre, { idx }),
        ...g.values.map((v) => cell.number(v, { editable: false, idx })),
        cell.aggregate(g.values.reduce((a, v) => a + v, 0), { kind: 'year' }),
      ],
    }));
    const totalRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('TOTAL'),
        cell.totalBlank(),
        ...(showGlName ? [cell.totalBlank()] : []),
        cell.totalBlank(),
        ...colTotals.map((t) => cell.totalNumber(t)),
        cell.totalNumber(total),
      ],
    };
    return [header, ...data, totalRow];
  }, [grouped, fyStartMonth, siteLabel, showGlName]);

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 mb-1.5 text-left group"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200">
          {title}
        </span>
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {grouped.length} posting{grouped.length === 1 ? '' : 's'} ·{' '}
          {grand.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </button>

      {open && (
        /* Height is computed, not flexed. The +44 covers the hint strip and
           +H_SCROLLBAR the horizontal one: the month columns overflow any
           realistic viewport, and the scrollbar is drawn INSIDE this height —
           without the allowance it sits on top of the TOTAL row, which is the
           row people came to read. */
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
          style={{ height: GRID.size.headerRow + Math.max(1, grouped.length) * GRID.size.dataRow + GRID.size.totalRow + 44 + H_SCROLLBAR }}>
          <MantleGrid rows={rows} columns={columns} hint="Read-only rollup — how this page's budget posts to the ledger by origin site, GL account and cost centre, per period." />
        </div>
      )}
    </div>
  );
};

export default PostingSummary;
